import { useEffect, useState } from 'react';

import { errors, isValid, validator } from 'react-validator';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TeacherGetQuestionByIdQuery } from '@school_ify/schoolify-repository/src/teacher/types';

import ThemeCheckBox from 'components/Input/CheckBox/ThemeCheckBox';
import quizIconBlueSmall from 'assets/icons/quiz-icon-blue-small.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { ReactComponent as PlusW } from 'assets/icons/plus_green.svg';
import Button from 'clean/components/common/Button/Button';
import { CreateEditQuestionModel } from 'types/quiz';

import QuizQuestionTitleContent from '../QuizQuestionTitleContent/QuizQuestionTitleContent';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './MultipleOptionsQuestion.scoped.scss';

interface MultipleOptionsQuestionProps {
  createOrUpdateQuestion: (question: CreateEditQuestionModel) => void;
  editQuestion?: TeacherGetQuestionByIdQuery['assignment_questions_by_pk'] | null;
}

const MultipleOptionsQuestion = (props: MultipleOptionsQuestionProps) => {
  const { createOrUpdateQuestion, editQuestion } = props;
  const [title, setTitle] = useState<string>();
  const [content, setContent] = useState<string>();
  const [validation, setValidation] = useState({});
  const { t } = useTranslation();
  const history = useHistory();

  const [rowsData, setRowsData] = useState([
    {
      is_correct: false,
      title: '',
      comment: '',
      score: null,
    },
  ]);

  useEffect(() => {
    if (editQuestion?.id) {
      setTitle(editQuestion.title);
      setContent(editQuestion.content);
      setRowsData(
        editQuestion.assignment_question_options.map(option => {
          return {
            id: option.id,
            is_correct: option.is_correct,
            title: option.title,
            comment: option.comment,
            score: option.score,
          };
        })
      );
    }
  }, [editQuestion]);

  const addTableRows = () => {
    const rowsInput = {
      is_correct: false,
      title: '',
      comment: '',
      score: null,
    };
    setRowsData([...rowsData, rowsInput]);
  };

  const onCreate = () => {
    const model = { title };
    validator(
      { model },
      {
        'model.title': ['required', 'string'],
      },
      {
        'model.title.required': t('study:title_required'),
      }
    );

    setValidation(errors());
    if (!isValid()) {
      return;
    }
    createOrUpdateQuestion({ title, content, questionOptions: rowsData });
  };

  const deleteTableRows = index => {
    const rows = [...rowsData];
    rows.splice(index, 1);
    setRowsData(rows);
  };

  const handleChange = (index, evnt) => {
    const { name, value, checked } = evnt.target;
    const rowsInput = [...rowsData];
    if (name === 'is_correct') rowsInput[index][name] = checked;
    if (name !== 'is_correct') rowsInput[index][name] = value;
    setRowsData(rowsInput);
  };

  return (
    <div className="quizquestion-container">
      <div className="create-quiz-center">
        <div className="title-container">
          <h2 className="new-quiz-title">
            <img alt="Quiz icon blue" src={quizIconBlueSmall} width="21" height="21" />
            {t('quiz:QuestionTypes.OneOrMore')}
          </h2>
        </div>

        <div className="quiz-components-wrapper">
          <QuizQuestionTitleContent
            onTitleChange={value => setTitle(value)}
            onContentChange={value => setContent(value)}
            title={title}
            content={content}
            validation={validation}
          />

          <div className="questions-table">
            <table>
              <tr>
                <th></th>
                <th>{t('quiz:Question.True')}</th>
                <th>{t('quiz:Question.Answer')}</th>
                <th>{t('quiz:Question.QuizComment')}</th>
                <th>{t('quiz:Question.Score')}</th>
                <th></th>
              </tr>
              {rowsData.map((data, index) => {
                const { is_correct, title, comment, score } = data;
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <ThemeCheckBox
                        name={'is_correct'}
                        checked={is_correct}
                        color="orange"
                        onChange={evnt => handleChange(index, evnt)}
                        style={{ background: 'red' }}
                        spanStyle={{ borderRadius: '4px' }}
                      />
                    </td>
                    <td>
                      <input
                        value={title}
                        onChange={evnt => handleChange(index, evnt)}
                        type="text"
                        placeholder={t('quiz:Question.EnterAnswerPlaceholder')}
                        name="title"
                        className="answer-input"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder={t('quiz:Question.CommentPlaceholder')}
                        value={comment}
                        onChange={evnt => handleChange(index, evnt)}
                        name="comment"
                        className="answer-input"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        placeholder="0"
                        value={score}
                        onChange={evnt => handleChange(index, evnt)}
                        name="score"
                        className="answer-input score"
                      />
                    </td>
                    <td>
                      <Trash
                        className="trash"
                        color="#213960"
                        width={16}
                        height={16}
                        onClick={() => deleteTableRows(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </table>
          </div>

          <div className="plus" onClick={addTableRows}>
            <PlusW />
          </div>
        </div>
      </div>
      <div className="button-wrapper">
        <Button
          onClick={() => onCreate()}
          label={t('quiz:Question.Create')}
          variant="filled"
          color="primary"
        />
        <Button onClick={() => history.goBack()} label={t('common:cancel')} color="decline" />
      </div>
    </div>
  );
};

export default MultipleOptionsQuestion;
